import React, { Component } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import throttle from 'lodash/throttle';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const IFrame = styled.iframe({
	border: 'none',
	margin: 0,
	width: '100%',
});

type IFrameLoaderProps = {
	src: string;
	sameOriginResize: boolean;
	resizePoll: boolean;
	resizePollInterval: number;
	title: string;
	onIFrameLoaded?: () => void;
};

// eslint-disable-next-line jira/react/no-class-components
export default class IFrameLoader extends Component<IFrameLoaderProps> {
	static defaultProps = {
		sameOriginResize: false,
		resizePoll: false,
		resizePollInterval: 500,
	};

	componentWillUnmount() {
		this.removeResizeListener();
		this.removeResizePolling();
	}

	onLoaded = () => {
		this.props.onIFrameLoaded && this.props.onIFrameLoaded();

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (this.props.sameOriginResize && this.props.src.startsWith(window.location.origin)) {
			this.resizeIFrameHeight();

			this.removeResizeListener();

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.addEventListener('resize', this.resizeIFrameHeight);
			this.removeResizeListener = () =>
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.removeEventListener('resize', this.resizeIFrameHeight);

			this.removeResizePolling();
			this.beginResizePolling();
		}
	};

	setIFrameRef = (ref: HTMLIFrameElement | null) => {
		this.iFrameRef = ref;
		this.resizeIFrameHeight();
		this.beginResizePolling();
	};

	iFrameRef: HTMLIFrameElement | null | undefined;

	removeResizeListener = noop;

	removeResizePolling = noop;

	beginResizePolling = () => {
		if (this.props.resizePoll) {
			this.removeResizePolling();
			const intervalId = setInterval(this.resizeIFrameHeight, this.props.resizePollInterval);
			this.removeResizePolling = () => clearInterval(intervalId);
		}
	};

	resizeIFrameHeight = throttle(() => {
		if (
			this.iFrameRef &&
			this.iFrameRef.contentWindow &&
			this.iFrameRef.contentWindow.document &&
			this.iFrameRef.contentWindow.document.body
		) {
			// we dive into the iframe's body – we can do this as the article contents are proxied through Jira
			// the body is the right container on all browsers – documentElement is wrong on Safari, hence its not being used
			const iframeBody = this.iFrameRef.contentWindow.document.body;
			// clientHeight has the correct content height – scrollHeight only ever increases on Safari
			//       that is to say, it doesn't reflect the content height but the container height, which we're setting here
			// iframe.height = ... sets the container height so the content height is left unaffected, so decreases can be detected
			this.iFrameRef.height = `${iframeBody.clientHeight}px`;
			// overflowY = 'hidden' forces Safari to obey the iframe height without adding a scrollbar
			//       without it, it adds the scrollbar which reduces the width + increases the height which
			//       removes the scrollbar which increases the width + reduces the height which
			//       ... yeah it's basically an infinite loop of pure pain
			iframeBody.style.overflowY = 'hidden';
		}
	});

	render() {
		const { src, title } = this.props;

		return (
			<IFrame title={title} ref={this.setIFrameRef} key={src} src={src} onLoad={this.onLoaded} />
		);
	}
}
